/**
 * Pair-proposal queue. Populated when the auto-matcher finds an account
 * whose best partner is NOT a perfect match — the operator decides
 * whether to accept the imperfect pairing or leave the account orphan.
 *
 * Deltas are rendered as ✅ / ⚠️ rows so the risk is obvious before
 * clicking approve.
 */

import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "@/components/ui/icons";
import { DashboardSection } from "@/components/dashboard-section";
import { FirmLogo } from "@/components/firm-logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/page-header";
import { CoolIcon } from "@/components/ui/cool-icon";
import {
  listPairProposals,
  approvePairProposal,
  rejectPairProposal,
  type PairProposalListItem,
  type PairDeltaEntry,
} from "@/lib/api/pair-proposals";
import { ApiError } from "@/lib/api";
import { useConfirm } from "@/components/ui/dialog-imperative";

function DeltaRow({ entry }: { entry: PairDeltaEntry }): ReactNode {
  const fmt = (v: string | number | null): string =>
    v == null ? "—" : typeof v === "number" ? String(v) : v;
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs">
      <span className="text-muted-foreground">{entry.field}</span>
      <span className="flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5 font-mono">
        <span className={entry.match ? "text-foreground" : "text-amber-500"}>
          {fmt(entry.a)}
        </span>
        <span className="text-muted-foreground">vs</span>
        <span className={entry.match ? "text-foreground" : "text-amber-500"}>
          {fmt(entry.b)}
        </span>
        {entry.diff != null && !entry.match && (
          <span className="text-[10px] text-amber-500">Δ {entry.diff}</span>
        )}
        <span aria-hidden>{entry.match ? "✅" : "⚠️"}</span>
      </span>
    </div>
  );
}

function ProposalCard({
  proposal,
  onDecided,
}: {
  proposal: PairProposalListItem;
  onDecided: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApprove = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const res = await approvePairProposal(proposal.slug);
      if (res.result?.accountPairSlug) {
        navigate(`/pairs/${encodeURIComponent(res.result.accountPairSlug)}`);
        return;
      }
      onDecided();
    } catch (e) {
      setError(
        e instanceof ApiError
          ? (e.body as { message?: string })?.message ?? e.message
          : e instanceof Error
            ? e.message
            : String(e),
      );
    } finally {
      setBusy(false);
    }
  };
  const handleReject = async (): Promise<void> => {
    const ok = await confirm({
      title: "Reject This Pair Proposal?",
      description: "It won’t be offered again.",
      confirmText: "Reject",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await rejectPairProposal(proposal.slug);
      onDecided();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardSection>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <FirmLogo
              src={proposal.candidateA.firmLogo}
              name={proposal.candidateA.firmName}
              className="h-10 w-10 shrink-0 rounded-lg border border-border object-contain p-1"
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">
                {proposal.candidateA.firmName}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {proposal.candidateA.login
                  ? `#${proposal.candidateA.login}`
                  : "—"}{" "}
                · {proposal.candidateA.phase}
              </p>
            </div>
          </div>
          <span
            className="ci-Arrow_Left_Right self-center text-xl text-muted-foreground sm:self-auto"
            aria-hidden
          />
          <div className="flex min-w-0 items-center gap-3 sm:flex-row-reverse">
            <FirmLogo
              src={proposal.candidateB.firmLogo}
              name={proposal.candidateB.firmName}
              className="h-10 w-10 shrink-0 rounded-lg border border-border object-contain p-1"
            />
            <div className="min-w-0 sm:text-right">
              <p className="truncate text-sm font-medium text-foreground">
                {proposal.candidateB.firmName}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {proposal.candidateB.login
                  ? `#${proposal.candidateB.login}`
                  : "—"}{" "}
                · {proposal.candidateB.phase}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge
            variant={proposal.quality === "near" ? "warning" : "destructive"}
            className="text-[10px] capitalize"
          >
            {proposal.quality === "near" ? "Near Match" : "Mismatch"}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {proposal.deltas.filter((d) => !d.match).length} rule differences
          </span>
        </div>

        <div className="flex flex-col gap-1 rounded-lg border border-border p-3">
          {proposal.deltas.map((d) => (
            <DeltaRow key={d.field} entry={d} />
          ))}
        </div>

        {error && <p aria-live="polite" className="text-xs text-destructive">{error}</p>}

        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" disabled={busy} onClick={handleReject}>
            Reject
          </Button>
          <Button size="sm" disabled={busy} onClick={handleApprove} aria-busy={busy}>
            {busy && (
              <Loader2 aria-hidden="true" className="mr-1 h-3 w-3 animate-spin" />
            )}
            {busy ? "Working…" : "Approve — I Accept The Risk"}
          </Button>
        </div>
      </div>
    </DashboardSection>
  );
}

export function PairProposalsPage(): ReactNode {
  const [proposals, setProposals] = useState<PairProposalListItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = (): void => {
    listPairProposals()
      .then(setProposals)
      .catch((e) => {
        setProposals([]);
        setLoadError(e instanceof Error ? e.message : String(e));
      });
  };
  useEffect(load, []);

  if (proposals === null) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 aria-label="Loading proposals" className="h-5 w-5 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 font-sans">
      <PageHeader
        title="Pair Approvals"
        subtitle="Approve Only The Imperfect Pairs Whose Rule Deltas You’ll Accept — Every Decision Is Logged."
        icon={<CoolIcon name="Check" />}
        accentWords={["Approvals"]}
      />

      {loadError && (
        <DashboardSection>
          <p aria-live="polite" className="text-sm text-destructive">{loadError}</p>
        </DashboardSection>
      )}

      {proposals.length === 0 ? (
        <DashboardSection>
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <p className="text-sm font-medium text-foreground">
              No Pending Approvals
            </p>
            <p className="mt-1 max-w-sm text-xs text-muted-foreground">
              All accounts are either paired or have no candidate partner.
              Return to the <Link to="/" className="text-accent">Account Pairs</Link>{" "}
              view.
            </p>
          </div>
        </DashboardSection>
      ) : (
        proposals.map((p) => (
          <ProposalCard key={p.id} proposal={p} onDecided={load} />
        ))
      )}
    </div>
  );
}
